import { Button } from 'src/components/Button/Button';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { Form, Text } from 'src/core/forms';
import { assign, createMachine, fromPromise } from 'xstate';
import { useMachine } from '@xstate/react';
import { firstValueFrom } from 'rxjs';
import { throwError } from 'rxjs';
import { useDispatch } from 'react-redux';
import { DataStatus } from 'src/core/engine/types';
import { CartMsg } from 'src/core/state/cart';
import { useFieldError } from 'src/core/forms';
import { isPositiveFloat } from 'src/components/Forms/QtySelect';
import { depsToDepsWithCspToken, unimplementedAction } from 'src/components/CustomerServicePortal/utils/utils';
import { MessagesModal } from 'src/components/Modal/MessagesModal';
import { errorMessage } from 'src/util/state-helpers';
import { useDeps } from 'src/hooks/useDeps';
import { execute } from 'swagger/ts/WorkwearExpressSalesAgentQuoteItemDiscountServiceV1ApplyDiscountPost';
import { ErrorFor } from 'src/components/Forms/ErrorFor';
import { getGlobalInspector } from 'src/components/Inspect/Inspect';

import { BasketCspContext } from '../Basket/BasketCspContext';

import classes from './BasketItemDiscount.scss';

type Context = {
    itemId: number | null;
    discount: number | null;
    error: string | null;
};

// prettier-ignore
type Events =
    | { type: 'ENTER_DISCOUNT'; itemId: number }
    | { type: 'UPDATE_ITEM'; value: number }
    | { type: 'REMOVE_DISCOUNT' }
    | { type: 'CANCEL' }
    | { type: 'DISMISS_ERROR' }
    | { type: 'CART_REFRESHED' };

export const DISCOUNT_FIELD = 'csr_discount';
export const DISCOUNT_FIELD_ERROR = 'percent-float';
export const DISCOUNT_FIELD_MSG = 'Must be between 1 and 100';

type MachineInput = Context & { token: string | null; deps: ReturnType<typeof useDeps> };
type ApplyUpdateInput = Context & { token: string; deps: ReturnType<typeof useDeps> };

const itemDiscountMachine = createMachine({
    id: 'itemDiscount',
    initial: 'idle',
    context: ({ input }: { input: MachineInput }) => ({ ...input }),
    states: {
        idle: {
            on: {
                ENTER_DISCOUNT: { target: 'enteringDiscount', actions: 'assignItemId' },
            },
        },
        enteringDiscount: {
            on: {
                CANCEL: 'idle',
                UPDATE_ITEM: { target: 'updatingItem', actions: 'assignDiscount' },
                REMOVE_DISCOUNT: { target: 'updatingItem', actions: 'assignDiscountZero' },
            },
        },
        updatingItem: {
            invoke: {
                src: fromPromise(({ input }: { input: ApplyUpdateInput }) => {
                    if (!input.itemId) throw new Error('itemId missing from context');
                    if (input.discount === null || Number.isNaN(input.discount))
                        throw new Error('ctx.discount not a number');
                    if (!input.token) throw new Error('token missing');
                    if (input.discount === 66.12) {
                        return Promise.reject(new Error('This is a test error message'));
                    }
                    const localDeps = depsToDepsWithCspToken(input.token, input.deps);
                    return firstValueFrom(
                        execute({ discountPercentage: input.discount, itemId: input.itemId }, localDeps),
                    );
                }),
                input: ({ context }: { context: MachineInput }) => ({
                    itemId: context.itemId,
                    discount: context.discount,
                    error: context.error,
                    token: context.token!,
                    deps: context.deps!,
                }),
                onDone: 'waitingForCartRefresh',
                onError: { target: 'displayError', actions: 'assignError' },
            },
        },
        waitingForCartRefresh: {
            entry: ['reduxCartRefresh'],
            on: {
                CART_REFRESHED: 'idle',
            },
        },
        displayError: {
            on: {
                DISMISS_ERROR: 'enteringDiscount',
            },
        },
    },
}).provide({
    actions: {
        assignItemId: assign({ itemId: ({ event }) => (event as Evt<'ENTER_DISCOUNT'>).itemId }),
        assignDiscount: assign({ discount: ({ event }) => (event as Evt<'UPDATE_ITEM'>).value }),
        assignDiscountZero: assign({ discount: () => 0 }),
        assignError: assign({
            error: ({ event }: { event: { error?: Error } }) => (event.error as any)?.message ?? null,
        }),
        reduxCartRefresh: unimplementedAction('reduxCartRefresh'),
    },
});

type Props = {
    itemId: number;
    discount: number;
    devTools?: boolean;
};

export function ItemDiscount(props: Props) {
    const deps = useDeps();
    const dispatch = useDispatch();
    const { token } = useContext(BasketCspContext);

    const machine = useMemo(
        () =>
            itemDiscountMachine.provide({
                actions: {
                    reduxCartRefresh: () => {
                        dispatch(CartMsg('Cart.Refresh', { force: true }));
                    },
                },
            }),
        [dispatch],
    );

    const inspector = getGlobalInspector();
    const [state, send] = useMachine(machine, {
        input: {
            itemId: props.itemId,
            discount: props.discount,
            error: null,
            token,
            deps,
        },
        ...(props.devTools && inspector ? { inspect: inspector.inspect } : {}),
    });

    useEffect(() => {
        const store = deps.getStore();
        let prev: DataStatus | undefined;
        const h = store.subscribe(() => {
            const cartStatus = store.getState().cart?.status;
            if (cartStatus === DataStatus.Success && prev === DataStatus.Pending) {
                send({ type: 'CART_REFRESHED' });
            }
            prev = cartStatus;
        });
        return () => h();
    }, [deps, send]);

    const discounting = ['enteringDiscount', 'updatingItem', 'displayError', 'waitingForCartRefresh'].some(
        state.matches,
    );
    // xstate v5: `state.matches` needs its `this` binding, so wrap in an arrow
    // rather than passing the method directly to Array.prototype.some.
    const hasError = ['displayError'].some((s) => state.matches(s));
    const isPending = ['updatingItem', 'waitingForCartRefresh'].some((s) => state.matches(s));
    const id = `${props.itemId}-discount`;

    const onSubmit = useCallback(
        (values: any) => {
            if (isPositiveFloat(values[DISCOUNT_FIELD])) {
                send({ type: 'UPDATE_ITEM', value: parseFloat(values[DISCOUNT_FIELD]) });
            }
        },
        [send],
    );

    const remove = useCallback(() => {
        send({ type: 'REMOVE_DISCOUNT' });
    }, [send]);

    return (
        <>
            {hasError && (
                <MessagesModal
                    close={() => send({ type: 'DISMISS_ERROR' })}
                    messages={[errorMessage((state.context as unknown as Context).error || 'unknown')]}
                    title="Oops!"
                />
            )}
            <div className={classes.cta}>
                {!discounting && (
                    <Button
                        variant="outline"
                        onClick={() => send({ type: 'ENTER_DISCOUNT', itemId: Number(props.itemId) })}
                        className={classes.outlineBtn}
                    >
                        {props.discount > 0 && 'Adjust Discount'}
                        {props.discount === 0 && 'Discount this item'}
                    </Button>
                )}
                {discounting && (
                    <Button variant="outline" onClick={() => send({ type: 'CANCEL' })}>
                        Close
                    </Button>
                )}
            </div>
            {discounting && (
                <div className={classes.controls}>
                    <p className={classes.title}>Add a discount to this item</p>
                    <Form
                        onSubmit={onSubmit}
                        initialValues={{ [DISCOUNT_FIELD]: props.discount > 0 ? props.discount : undefined }}
                        data-test-id="ItemDiscount_Form"
                    >
                        <fieldset disabled={isPending}>
                            <FormInner id={id} remove={remove} />
                        </fieldset>
                    </Form>
                </div>
            )}
        </>
    );
}

type FormProps = {
    id: string;
    remove: () => void;
};

const FormInner = React.memo(function FormInner(props: FormProps) {
    const error = useFieldError(DISCOUNT_FIELD);
    return (
        <div className={classes.form} data-test-id="ItemDiscount_Form_inner">
            <Text
                field={DISCOUNT_FIELD}
                id={props.id}
                type="number"
                step="0.01"
                min="0"
                className={classes.input}
                validate={percentageFloat}
                validateOnChange={true}
                validateOnBlur={true}
                data-has-error={Boolean(error)}
            />
            <label className={classes.label} htmlFor={props.id}>
                % Discount
            </label>
            {error && (
                <ErrorFor
                    errors={{
                        [DISCOUNT_FIELD]: {
                            [DISCOUNT_FIELD_ERROR]: DISCOUNT_FIELD_MSG,
                        },
                    }}
                    field={DISCOUNT_FIELD}
                    render={(message) => <div className={classes.error}>{message}</div>}
                />
            )}
            <div className={classes.buttons}>
                <Button variant="reset" className={classes.removeBtn} onClick={props.remove}>
                    Remove Discount
                </Button>
                <Button variant="primary" className={classes.updateBtn} type="submit">
                    Update
                </Button>
            </div>
        </div>
    );
});

function percentageFloat(value: any, other: any) {
    const float = isPositiveFloat(value);
    if (float) {
        const parsed = parseFloat(value);
        if (parsed >= 1 && parsed <= 100) {
            return undefined;
        }
    }
    return DISCOUNT_FIELD_ERROR;
}

export type Evt<M extends Events['type']> = Extract<Events, { type: M }>;
